import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { FileSpreadsheet } from 'lucide-react';
import type { DeliveryVoucherIndicatorType } from 'contracts/DeliveryVouchers';
import type {
  DeliveryVoucher,
  InvoiceDeliveryVoucher,
} from 'contracts/DeliveryVouchers';
import { notify } from 'services/notifications';
import {
  useDeliveryVoucher,
  useFetchDeliveryVoucherInvoices,
} from 'hooks/api/delivery-vouchers';
import {
  useDeliveryVouchersSearchParams,
  type DeliveryVoucherFilters as Filters,
} from 'hooks/store/delivery-vouchers';
import { usePermission } from 'hooks/usePermission';
import { DELIVERY_VOUCHERS_PERMISSIONS } from 'constants/delivery-vouchers';
import { Scaffold } from 'layouts';
import { Button } from 'components/ui/button';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTableV2, type SortingProps } from 'components/ui/DataTableV2';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';
import {
  ClearFiltersButton,
  TabbedNavigation,
} from 'pages/tracking-v2/shared-components';
import { RootState } from 'store';
import {
  DeleteDeliveryVoucherActions,
  UpdateDeliveryVoucherActions,
} from 'store/ducks/deliveryVouchers';

import { useDeliveryVoucherColumns } from './hooks';
import {
  DeliveryVoucherFilters,
  type DeliveryVoucherFiltersRef,
  DeliveryVoucherIndicators,
  PageTitle,
  UploadDeliveryVoucherDialog,
  type UploadDeliveryVoucherDialogRef,
  ViewDeliveryVoucherDialog,
  type ViewDeliveryVoucherDialogRef,
} from './components';
import { hasActiveDeliveryVoucherFilters } from './utils';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RejectionProfDialog,
  RejectionProfDialogRef,
} from '../invoice-details-page/components/rejection-voucher-dialog';
import { RejectionProfPayload } from 'contracts/delivery-vouchers/delivery-voucher-justifications';
import { useDeliveryVoucherStore } from '../invoice-details-page/hooks/use-delivery-voucher-store';

/**
 * Fluxo da tela:
 * 1. Lê filtros/paginação persistidos.
 * 2. Busca listagem e indicadores no mesmo endpoint.
 * 3. Encaminha ações da tabela para dialogs, sagas ou download/exportação.
 * 4. Atualiza a consulta após ações que mudam o comprovante.
 */
export const DeliveryVouchersPage = () => {
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useDeliveryVouchersSearchParams();
  const dispatch = useDispatch();
  const { hasPermissionTo } = usePermission();
  const { fetchDeliveryVoucherInvoices, exportDeliveryVoucherInvoices } =
    useFetchDeliveryVoucherInvoices();
  const { downloadVoucher } = useDeliveryVoucher();
  const filtersRef = React.useRef<DeliveryVoucherFiltersRef>(null);
  const uploadDialogRef = React.useRef<UploadDeliveryVoucherDialogRef>(null);
  const viewDialogRef = React.useRef<ViewDeliveryVoucherDialogRef>(null);
  const rejectDialogRef = React.useRef<RejectionProfDialogRef>(null);
  const confirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);
  const { setDeliveryVoucher, resetDeliveryVoucherStoreState } =
    useDeliveryVoucherStore();

  const { loading: creatingDeliveryVoucher } = useSelector(
    (state: RootState) => state.createDeliveryVoucher
  );
  const { loading: updatingDeliveryVoucher } = useSelector(
    (state: RootState) => state.updateDeliveryVoucher
  );
  const { loading: deletingDeliveryVoucher } = useSelector(
    (state: RootState) => state.deleteDeliveryVoucher
  );

  const {
    mutate: downloadDeliveryVoucher,
    isLoading: downloadingDeliveryVoucher,
  } = useMutation(downloadVoucher);

  const exportDeliveryVouchersMutation = useMutation({
    mutationKey: ['export-delivery-voucher-invoices'],
    mutationFn: (exportFilters: Filters) =>
      exportDeliveryVoucherInvoices(exportFilters),
    onSuccess: () => {
      notify('success', 'Relatório de comprovantes gerado com sucesso.');
    },
    onError: (error: any) => {
      notify(
        'error',
        error?.message || 'Não foi possível exportar comprovantes.'
      );
    },
  });

  const someFiltersAreActive = hasActiveDeliveryVoucherFilters(filters);
  const canExport = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.export);
  const canDownload = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.download);
  const canUpdate = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.update);
  const canDelete = hasPermissionTo(DELIVERY_VOUCHERS_PERMISSIONS.delete);
  const location = useLocation();
  const navigate = useNavigate();

  const deliveryVouchersQuery = useQuery({
    queryKey: ['fetch-delivery-voucher-invoices', filters, paginationControl],
    queryFn: () => {
      return fetchDeliveryVoucherInvoices({
        filters,
        pagination: paginationControl,
      });
    },
    refetchOnWindowFocus: false,
  });

  const handleRequestSuccess = React.useCallback(() => {
    // O endpoint devolve grade e indicadores juntos, portanto um único refetch atualiza a tela.
    void deliveryVouchersQuery.refetch();
  }, [deliveryVouchersQuery]);

  const handleOpenUpload = React.useCallback(
    (invoice: InvoiceDeliveryVoucher) => {
      uploadDialogRef.current?.open(invoice);
    },
    []
  );

  const handleOpenView = React.useCallback((voucher: DeliveryVoucher) => {
    viewDialogRef.current?.open(voucher);
  }, []);

  const handleDownload = React.useCallback(
    (voucher: DeliveryVoucher) => {
      downloadDeliveryVoucher(voucher.id);
    },
    [downloadDeliveryVoucher]
  );

  const handleApproveVoucher = React.useCallback(
    async (voucher: DeliveryVoucher) => {
      const confirmed = await confirmationDialogRef.current?.openDialog({
        title: 'Aprovar comprovante',
        message: 'Deseja realmente aprovar este comprovante?',
      });

      if (confirmed !== 'confirmed') return;

      dispatch(
        UpdateDeliveryVoucherActions.request(
          voucher.id,
          { action: 'approve' },
          handleRequestSuccess
        )
      );
    },
    [dispatch, handleRequestSuccess]
  );

  const handleCancelApprovalVoucher = React.useCallback(
    async (voucher: DeliveryVoucher) => {
      const confirmed = await confirmationDialogRef.current?.openDialog({
        title: 'Cancelar aprovação',
        message: 'Deseja realmente cancelar a aprovação deste comprovante?',
      });

      if (confirmed !== 'confirmed') return;

      dispatch(
        UpdateDeliveryVoucherActions.request(
          voucher.id,
          { action: 'cancelApproval' },
          handleRequestSuccess
        )
      );
    },
    [dispatch, handleRequestSuccess]
  );

  const handleRejectVoucher = React.useCallback(
    (payload: RejectionProfPayload, callback: () => void) => {
      const { deliveryVoucherId, ...rest } = payload;

      dispatch(
        UpdateDeliveryVoucherActions.request(
          deliveryVoucherId,
          { action: 'reject', ...rest },
          () => {
            rejectDialogRef.current?.close();
            handleRequestSuccess();
            resetDeliveryVoucherStoreState();
            callback();
          }
        )
      );
    },
    [dispatch, handleRequestSuccess]
  );

  const handleDeleteVoucher = React.useCallback(
    async (voucher: DeliveryVoucher) => {
      const confirmed = await confirmationDialogRef.current?.openDialog({
        title: 'Excluir comprovante',
        message: 'Deseja realmente excluir este comprovante?',
      });

      if (confirmed !== 'confirmed') return;

      dispatch(
        DeleteDeliveryVoucherActions.request(voucher.id, handleRequestSuccess)
      );
    },
    [dispatch, handleRequestSuccess]
  );

  const actionsDisabled =
    creatingDeliveryVoucher ||
    updatingDeliveryVoucher ||
    deletingDeliveryVoucher ||
    downloadingDeliveryVoucher;

  const columns = useDeliveryVoucherColumns({
    onUpload: handleOpenUpload,
    onView: handleOpenView,
    onDownload: handleDownload,
    onApprove: handleApproveVoucher,
    onReject: (voucher) => {
      rejectDialogRef.current?.open();
      setDeliveryVoucher(voucher);
    },
    onCancelApproval: handleCancelApprovalVoucher,
    onDelete: handleDeleteVoucher,
    actionsDisabled,
    canDownload,
    canUpdate,
    canDelete,
  });

  const handleFilters = React.useCallback(
    (newFilters: Filters) => {
      updateFilters(newFilters);
      updatePaginationControl({ page: 1 });
    },
    [updateFilters, updatePaginationControl]
  );

  const handleIndicator = React.useCallback(
    (indicator?: DeliveryVoucherIndicatorType) => {
      const { filterIndicators, ...generalFilters } = filters;

      handleFilters({
        ...generalFilters,
        ...(indicator && { filterIndicators: indicator }),
      });
    },
    [filters, handleFilters]
  );

  const handleSort = React.useCallback(
    (sorting: SortingProps) => {
      updatePaginationControl({ ...sorting, page: 1 });
    },
    [updatePaginationControl]
  );

  const handleExport = React.useCallback(() => {
    if (!canExport) return;

    if (!deliveryVouchersQuery.data?.meta?.total) {
      notify('error', 'Não existem comprovantes para exportar.');
      return;
    }

    exportDeliveryVouchersMutation.mutate(filters);
  }, [
    canExport,
    deliveryVouchersQuery.data?.meta?.total,
    exportDeliveryVouchersMutation,
    filters,
  ]);

  React.useEffect(() => {
    const state = location.state as { action?: string; voucher?: DeliveryVoucher };
    
    if (state?.action === 'openVoucherModal' && state.voucher) {
      navigate(location.pathname, { replace: true, state: {} });
      
      handleOpenView(state.voucher);
    }
  }, [location.state, handleOpenView, navigate, location.pathname]);

  return (
    <Scaffold>
      <ConfirmationDialog ref={confirmationDialogRef} />
      <UploadDeliveryVoucherDialog
        ref={uploadDialogRef}
        onSuccess={handleRequestSuccess}
      />
      <ViewDeliveryVoucherDialog ref={viewDialogRef} />
      <RejectionProfDialog
        ref={rejectDialogRef}
        loading={updatingDeliveryVoucher}
        onSubmit={handleRejectVoucher}
      />
      <TabbedNavigation />
      <div className="flex flex-col gap-6">
        <PageHeaderForFilters
          headerTitle={({ open }) => (
            <>
              <PageTitle />
              <ClearFiltersButton
                visible={!open && someFiltersAreActive}
                onClick={filtersRef.current?.resetFilters}
              />
            </>
          )}
        >
          <DeliveryVoucherFilters
            ref={filtersRef}
            onSubmitFilters={handleFilters}
            onResetFilters={clearState}
          />
        </PageHeaderForFilters>

        <DeliveryVoucherIndicators
          indicators={deliveryVouchersQuery.data?.indicators}
          activeIndicator={filters.filterIndicators}
          loading={deliveryVouchersQuery.isLoading}
          onToggleIndicator={handleIndicator}
        />

        <DataTableV2
          data={deliveryVouchersQuery.data?.data ?? []}
          columns={columns}
          manualPagination
          showControlColumns
          inlineTopBarControls
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          isLoading={deliveryVouchersQuery.isLoading}
          renderTopBar={() =>
            canExport ? (
              <div className="ml-auto inline-flex h-fit w-fit shrink-0 rounded-md bg-white p-0">
                <Button
                  type="button"
                  size="sm"
                  className="h-9 bg-emerald-600 px-5 font-GilroySemibold text-white hover:bg-emerald-700"
                  disabled={
                    exportDeliveryVouchersMutation.isLoading ||
                    !deliveryVouchersQuery.data?.meta?.total
                  }
                  onClick={handleExport}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  {exportDeliveryVouchersMutation.isLoading
                    ? 'Exportando...'
                    : 'Exportar'}
                </Button>
              </div>
            ) : null
          }
          pagination={{
            ...deliveryVouchersQuery.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </div>
    </Scaffold>
  );
};
